import { CommonModule } from '@angular/common';
import { withRequestsMadeViaParent } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { debounceTime } from 'rxjs/operators';
import { TestStatusEnum } from 'src/app/core/enums';
import { MaterialModule } from 'src/app/core/material.module';
import { AbilityService } from 'src/app/core/services/ability/ability.service';
import { AppStatsStoreStoreService } from 'src/app/core/services/app-stats-store.service';
import { AppService } from 'src/app/core/services/app.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DeviceService } from 'src/app/features/devices/services/devices.service';
import { PaginationComponent } from 'src/app/shared/pagination/pagination.component';
import {
  ALL_DEVICE_STATUS_OPTIONS,
  DeviceStatus,
  deviceStatusLabel,
  isDeviceOccupied,
} from 'src/app/features/tests/features/constants';
import { DeviceStatusDialogComponent } from '../devices-status-dialog/devices-status-dialog.component';
import { AppDeviseSizeIndicatorComponent } from '../size-indicator/size-indicator.component';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator.component';
import { AppDeviseUsageIndicatorComponent } from "../usage-indicator/usage-indicator.component";
interface MetaData {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  totalPages: number;
  availableDevices: number;
  totalDevicesCount: number;
}

@Component({
  selector: 'app-devices-selector',
  imports: [
    CommonModule,
    MaterialModule,
    ReactiveFormsModule,
    StatusIndicatorComponent,
    AppDeviseSizeIndicatorComponent,
    AppDeviseUsageIndicatorComponent,
    PaginationComponent
],
  templateUrl: './devices-selector.component.html',
  styleUrl: './devices-selector.component.scss',
})
export class DevicesSelectorComponent implements OnInit {
  @Input() containerHeight: string;

  @ViewChild('devicesList') private devicesList?: ElementRef<HTMLElement>;

  @Input() selectedDeviceId: string = undefined;
  @Input() className: string = '';
  @Input() statusFilter?: string = '';
  @Input() selectedDevice? = null;
  /**
   * Fill the height the host is given, the list taking what the heading and the
   * pagination footer leave, instead of capping the list at a viewport fraction.
   * For a host that already sizes the selector, such as a dialog tab.
   */
  @Input() fillHeight = false;
  @Output() deviceFormValue = new EventEmitter();
  isDashboard = false;
  devices: any[] = [];

  loader = false;
  currentFilter = 'all';
  availableCount = 0;
  totalDevicesCount = 0;
  deviseStatusEnum = DeviceStatus;
  isDeviceOccupied = isDeviceOccupied;
  deviceStatusLabel = deviceStatusLabel;
  departmentId: number | null = null;
  allDevicesStatusOptions = ALL_DEVICE_STATUS_OPTIONS;
  metaData: MetaData = {
    currentPage: 1,
    totalItems: 0,
    itemsPerPage: 30,
    totalPages: 0,
    availableDevices: 0,
    totalDevicesCount: 0,
  };

  searchControl = new FormControl('');

  constructor(
    public notificationService: NotificationService,
    public ability: AbilityService,
    private appService: AppService,
    private dialog: MatDialog,
    private devicesService: DeviceService,
    private appStatsStoreStoreService: AppStatsStoreStoreService,
  ) {}

  ngOnInit() {
    this.resetSelection();

    this.devicesService.eventEmitter.subscribe((d) => {
      if (d == 'reset') {
        this.resetSelection();
      }
    });

    this.appService.eventEmitter.subscribe((d) => {
      if (d == 'DEVICE:UPDATED_STATUS') {
        this.loadDevices(true);
      }
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        this.loadDevices();
      });

    this.appStatsStoreStoreService.departmentId$.subscribe((deptId) => {
      this.departmentId = deptId;
      this.loadDevices(true);
    });
  }

  resetSelection() {
    this.isDashboard = this.className === 'dashboard';
    this.currentFilter = this.statusFilter ? this.statusFilter : 'available';
    this.selectedDevice = null;
    this.searchControl.patchValue('');
    this.loadDevices();
  }

  loadDevices(reset: boolean = false) {
    if (reset) {
      this.metaData.currentPage = 1;
      this.devices = [];
    }

    this.loader = true;
    // A device that has been taken out of the active inventory cannot be picked
    // for a test, so it is left out of both tabs rather than shown and rejected
    // on save. The Devices screen is where it stays visible.
    const filters: Record<string, any> =
      this.currentFilter === 'available'
        ? { type: 'available', isActive: true }
        : { type: 'all', isActive: true };
    if (this.searchControl.value) {
      filters['q'] = this.searchControl.value;
    }
    if (this.departmentId) {
      filters['departmentId'] = this.departmentId;
    }
    const options = {
      ...filters,
      page: this.metaData.currentPage,
      limit: this.metaData.itemsPerPage,
    };

    this.devicesService.getDevice(options).subscribe({
      next: (res) => {
        this.metaData = res.meta;
        this.availableCount = this.metaData.availableDevices;
        this.totalDevicesCount = this.metaData.totalDevicesCount;
        this.devices = res.items;
        if (!this.selectedDevice && this.selectedDeviceId) {
          const device = this.devices.find(
            (d) => d.id == this.selectedDeviceId,
          );
          if (device) {
            this.selectedDevice = device;
          }
        }
        this.loader = false;
      },
      error: (error) => {
        this.loader = false;
        console.error('Error loading devices:', error);
      },
    });
  }

  selectDevice(device: any) {
    if (this.isDashboard) {
      return;
    }
    if (isDeviceOccupied(device.status)) {
      return;
    }
    this.selectedDevice = device;

    this.deviceFormValue.emit({
      valid: true,
      form: 'app-devices-selector',
      value: device,
    });
  }

  onAllStatusSelect(status: DeviceStatus): void {
    const type = this.currentFilter === 'available' ? 'available' : 'all';
    this.dialog
      .open(DeviceStatusDialogComponent, { data: { status, type } })
      .afterClosed()
      .subscribe((sel) => {
        this.loadDevices(true);
        this.appService.eventEmitter.emit('DEVICE:UPDATED_STATUS');
      });
  }

  onSelectedStatusSelect(device: any, status: DeviceStatus): void {
    const id = device.id;

    const updateDeviceStatus = (unAssignTest = undefined) => {
      device.statusCheckLoading = true;
      this.devicesService
        .updateStatus({ status, unAssignTest }, { type: 'specific', id })
        .subscribe({
          next: () => {
            device.statusCheckLoading = false;
            this.loader = false;

            this.devices = this.devices.map((m) => {
              if (m.id === id) {
                return { ...m, status };
              }
              return m;
            });

            this.appService.eventEmitter.emit('DEVICE:UPDATED_STATUS');
          },
          error: (error) => {
            device.statusCheckLoading = false;
            this.loader = false;
            console.error('Error updating device status:', error);
          },
        });
    };

    if (isDeviceOccupied(device.status)) {
      device.statusCheckLoading = true;
      this.devicesService.getDeviceCurrentStatus(id).subscribe({
        next: (res) => {
          const patient = res?.result?.patient;

          if (res?.result.testStatus == TestStatusEnum.RECORDING) {
            const message = patient
              ? `Please Note: This device is currently assigned to patient ${patient?.user?.fullName} (ID: ${patient?.patientId}). and the test is in recording status. Please End the test before changing the device status.`
              : `Please Note: This device is currently in recording status. Please End the test id: ${res?.result?.id} before changing the device status.`;
            this.notificationService.openDialog({
              title: 'Attention!',
              message: message,
              cancelButtonHide: true,
              confirmButtonText: 'Ok',
              onCancel: () => {},
              onConfirm: () => {},
            });
            device.statusCheckLoading = false;
            return;
          }
          if (!patient) {
            // No patient → directly update
            updateDeviceStatus();
            device.statusCheckLoading = false;
            return;
          }
          this.notificationService.openDialog({
            title: 'Are you sure?',
            message: `Please Note: This device is currently assigned to patient ${patient?.user?.fullName} (ID: ${patient?.patientId}). Changing the status will unassign the device for this patient. Are you sure?`,
            confirmButtonText: 'Yes',
            cancelButtonText: 'Cancel',
            onConfirm: () => {
              updateDeviceStatus(res?.result?.id);
            },
            onCancel: () => {},
          });
          device.statusCheckLoading = false;
        },
        error: () => {
          this.notificationService.error('Error in getting device info');
        },
      });

      return;
    }

    updateDeviceStatus();
  }

  public goToPage(page: number): void {
    if (this.loader || page === this.metaData.currentPage) {
      return;
    }

    this.metaData.currentPage = page;
    this.loadDevices();
    // Start the new page at its first device instead of wherever the previous
    // page was left scrolled to.
    if (this.devicesList) {
      this.devicesList.nativeElement.scrollTop = 0;
    }
  }

  public onPageSizeChange(size: number): void {
    if (this.loader || size === this.metaData.itemsPerPage) return;
    this.metaData.itemsPerPage = size;
    this.metaData.currentPage = 1;
    this.loadDevices();
    if (this.devicesList) {
      this.devicesList.nativeElement.scrollTop = 0;
    }
  }

  onFilterChange(filter: string) {
    this.currentFilter = filter;
    this.loadDevices(true);
  }

  protected readonly withRequestsMadeViaParent = withRequestsMadeViaParent;
}
